import * as React from "react";

import { LoadingButton } from "@mui/lab";
import { Alert } from "@mui/material";

import { UserContext } from "context/usercontext";
import useBridgeSWR from "src/common/hooks/BridgeSWRClient";
import { useFixDNS } from "src/domain/core/workflow/api/healthWorkflow";
import { IsInSuccessState } from "src/domain/core/workflow/components/active/Status";
import { endpoint } from "src/lib/bridgeClient";
import useStore, { DNSStatus } from "src/store/store";

interface DNSRecoveryProps {
  workflow: {
    id: string;
    status: string;
  };
}

export const DNSRecovery: React.FC<DNSRecoveryProps> = ({ workflow }) => {
  const me = React.useContext(UserContext);
  const workflowDnsExists = useStore((state) => state.updateDnsRecordStatus);
  const dnsRecordExists = useStore((state) => state.getDnsRecordStatus(workflow?.id));

  // Only need to perform health check once workflow marked as created
  const { data, error } = useBridgeSWR<WorkflowHealth>(
    IsInSuccessState(workflow.status) ? endpoint.workflowHealth(workflow?.id) : null
  );

  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const fix = useFixDNS(workflow.id);

  const onDNSFixSubmit = async () => {
    setSubmitting(true);
    const resp = await fix(data.master_server.instance_id, me.pool_id);
    // if we get a response means that the dns record was created
    if (resp) {
      workflowDnsExists(workflow.id, DNSStatus.EXIST);
    }

    setSubmitting(false);
  };

  React.useEffect(() => {
    if (data) {
      workflowDnsExists(
        workflow.id,
        data.master_server.health.dns_record_exists ? DNSStatus.EXIST : DNSStatus.NOT_EXIST
      );
    }
    if (error) {
      workflowDnsExists(workflow.id, DNSStatus.ERROR);
    }
  }, [data, error, workflow.id, workflowDnsExists]);

  const render = () => {
    if (IsInSuccessState(workflow.status)) {
      if (dnsRecordExists === DNSStatus.ERROR) {
        return (
          <Alert severity="warning">{"Unable to perform workflow health check. DNS record might not exist."}</Alert>
        );
      }
      if (dnsRecordExists === DNSStatus.NOT_EXIST) {
        return (
          <Alert severity="warning">
            {"Unable to launch applications. Missing DNS record. Click "}
            <LoadingButton
              disabled={submitting}
              loading={submitting}
              variant="outlined"
              color="warning"
              size="small"
              onClick={onDNSFixSubmit}
              sx={{ p: 0, m: 0 }}
            >
              Fix DNS
            </LoadingButton>
            {" to resolve."}
          </Alert>
        );
      }
    }

    return <></>;
  };

  return render();
};
